import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Badge, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import InstagramPostGraphic from "@/components/InstagramPostGraphic";
import InstagramCTA from "@/components/InstagramCTA";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const SocialAssets: React.FC = () => {
  const [activeTab, setActiveTab] = useState("survival-badge");
  
  const handleVisitSite = () => {
    window.open("https://saunadip.com/", "_blank");
  };
  
  return (
    <div className="container mx-auto my-8 px-4">
      <div className="mb-6">
        <Link to="/">
          <Button variant="ghost" className="pl-0">
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
        </Link>
      </div>

      <h1 className="text-3xl font-bold text-sauna-navy mb-6">Social Media Assets</h1>
      
      {/* Survival Badge CTA */}
      <div className="mb-6">
        <InstagramCTA 
          title="Create your 'I Survived' badge today!"
          description="Share your sauna experience with our custom badge. Show off your heat endurance and cold plunge bravery on Instagram. Create, download, and share with just a few clicks!"
          buttonText="Create Your Badge Now"
          logoImage="/assets/logos/saunadip-logo.svg"
          onAction={() => setActiveTab("survival-badge")}
        />
      </div>
      
      {/* Countdown CTA */}
      <div className="mb-12">
        <InstagramCTA 
          title="The Countdown is on at SaunaDip.com!"
          description="Only 10 days until we launch Ireland & UK's first sauna and plunge directory. Share the excitement with your followers and help spread the word!"
          buttonText="Share Countdown Now"
          variant="countdown"
          onAction={() => setActiveTab("countdown")}
        />
      </div>
      
      <div className="space-y-6 mb-10">
        <p className="text-sauna-navy/80 max-w-3xl">
          Share SaunaDip with your audience! Download these Instagram-ready graphics to help spread the word about Ireland & UK's first sauna and plunge directory.
        </p>
        
        <Tabs defaultValue="survival-badge" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8">
            <TabsTrigger value="survival-badge" className="flex items-center gap-2">
              <Badge size={18} />
              Survival Badge
            </TabsTrigger>
            <TabsTrigger value="countdown" className="flex items-center gap-2">
              <Clock size={18} />
              Countdown
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="survival-badge" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Create Your Survival Badge</h2>
                <p className="text-sauna-navy/80">
                  Share your sauna survival experience on Instagram with this custom badge. Show off your temperature endurance and encourage others to create their own!
                </p>
                <div className="p-4 bg-sauna-orange/10 rounded-lg border border-sauna-orange/20">
                  <h3 className="font-medium text-sauna-navy mb-2">How to use this graphic:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sauna-navy/80">
                    <li>Download the image using the button below</li>
                    <li>Upload it to your Instagram feed</li>
                    <li>Add a caption about your sauna experience</li>
                    <li>Tag @saunadip and use #SaunaDip hashtag</li>
                    <li>Encourage followers to create their own badge</li>
                  </ol>
                </div>
              </div>
              
              <div>
                <InstagramPostGraphic variant="survival-badge" />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="countdown" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-sauna-navy">Share The Countdown</h2>
                <p className="text-sauna-navy/80">
                  Let your followers know about SaunaDip's upcoming launch! Build anticipation for Ireland & UK's first sauna and plunge directory.
                </p>
                <div className="p-4 bg-sauna-blue/10 rounded-lg border border-sauna-blue/20">
                  <h3 className="font-medium text-sauna-navy mb-2">How to use this graphic:</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sauna-navy/80">
                    <li>Download the image using the button below</li>
                    <li>Upload it to your Instagram feed or story</li>
                    <li>Add a caption about why you're excited for SaunaDip</li>
                    <li>Tag @saunadip and use #SaunaDip hashtag</li>
                    <li>Encourage followers to visit the website</li>
                  </ol>
                </div>
              </div>
              
              <div>
                <InstagramPostGraphic variant="countdown" />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SocialAssets;
